import React, { useState, useEffect, useRef, useContext } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import Footer from './Footer';
import envConfig from '../envConfig';

// ── Design tokens ─────────────────────────────────────────────────────────────
const T = { navy: '#12324f', navy2: '#1c4467', ink: '#1f2a33', muted: '#6b7a86', line: '#e1e6ea', panel: '#f6f8f9' };

// ── Country config ─────────────────────────────────────────────────────────────
const COUNTRY_CONFIG = {
    1: { label: 'Malawi', center: [-13.2543, 34.3015], zoom: 7 },
    2: { label: 'Zambia', center: [-13.1339, 27.8493], zoom: 6 },
};

// ── Dekad index (DESC order: 36 → 1, with leap-year duplicate for Feb D3) ─────
const DEKAD_INDEX = [
    { dekad: 36, dekad_code: '12-D3', label: 'December 21 to 31',  month: 12, start_day: 21, end_day: 31 },
    { dekad: 35, dekad_code: '12-D2', label: 'December 11 to 20',  month: 12, start_day: 11, end_day: 20 },
    { dekad: 34, dekad_code: '12-D1', label: 'December 1 to 10',   month: 12, start_day: 1,  end_day: 10 },
    { dekad: 33, dekad_code: '11-D3', label: 'November 21 to 30',  month: 11, start_day: 21, end_day: 30 },
    { dekad: 32, dekad_code: '11-D2', label: 'November 11 to 20',  month: 11, start_day: 11, end_day: 20 },
    { dekad: 31, dekad_code: '11-D1', label: 'November 1 to 10',   month: 11, start_day: 1,  end_day: 10 },
    { dekad: 30, dekad_code: '10-D3', label: 'October 21 to 31',   month: 10, start_day: 21, end_day: 31 },
    { dekad: 29, dekad_code: '10-D2', label: 'October 11 to 20',   month: 10, start_day: 11, end_day: 20 },
    { dekad: 28, dekad_code: '10-D1', label: 'October 1 to 10',    month: 10, start_day: 1,  end_day: 10 },
    { dekad: 27, dekad_code: '09-D3', label: 'September 21 to 30', month: 9,  start_day: 21, end_day: 30 },
    { dekad: 26, dekad_code: '09-D2', label: 'September 11 to 20', month: 9,  start_day: 11, end_day: 20 },
    { dekad: 25, dekad_code: '09-D1', label: 'September 1 to 10',  month: 9,  start_day: 1,  end_day: 10 },
    { dekad: 24, dekad_code: '08-D3', label: 'August 21 to 31',    month: 8,  start_day: 21, end_day: 31 },
    { dekad: 23, dekad_code: '08-D2', label: 'August 11 to 20',    month: 8,  start_day: 11, end_day: 20 },
    { dekad: 22, dekad_code: '08-D1', label: 'August 1 to 10',     month: 8,  start_day: 1,  end_day: 10 },
    { dekad: 21, dekad_code: '07-D3', label: 'July 21 to 31',      month: 7,  start_day: 21, end_day: 31 },
    { dekad: 20, dekad_code: '07-D2', label: 'July 11 to 20',      month: 7,  start_day: 11, end_day: 20 },
    { dekad: 19, dekad_code: '07-D1', label: 'July 1 to 10',       month: 7,  start_day: 1,  end_day: 10 },
    { dekad: 18, dekad_code: '06-D3', label: 'June 21 to 30',      month: 6,  start_day: 21, end_day: 30 },
    { dekad: 17, dekad_code: '06-D2', label: 'June 11 to 20',      month: 6,  start_day: 11, end_day: 20 },
    { dekad: 16, dekad_code: '06-D1', label: 'June 1 to 10',       month: 6,  start_day: 1,  end_day: 10 },
    { dekad: 15, dekad_code: '05-D3', label: 'May 21 to 31',       month: 5,  start_day: 21, end_day: 31 },
    { dekad: 14, dekad_code: '05-D2', label: 'May 11 to 20',       month: 5,  start_day: 11, end_day: 20 },
    { dekad: 13, dekad_code: '05-D1', label: 'May 1 to 10',        month: 5,  start_day: 1,  end_day: 10 },
    { dekad: 12, dekad_code: '04-D3', label: 'April 21 to 30',     month: 4,  start_day: 21, end_day: 30 },
    { dekad: 11, dekad_code: '04-D2', label: 'April 11 to 20',     month: 4,  start_day: 11, end_day: 20 },
    { dekad: 10, dekad_code: '04-D1', label: 'April 1 to 10',      month: 4,  start_day: 1,  end_day: 10 },
    { dekad: 9,  dekad_code: '03-D3', label: 'March 21 to 31',     month: 3,  start_day: 21, end_day: 31 },
    { dekad: 8,  dekad_code: '03-D2', label: 'March 11 to 20',     month: 3,  start_day: 11, end_day: 20 },
    { dekad: 7,  dekad_code: '03-D1', label: 'March 1 to 10',      month: 3,  start_day: 1,  end_day: 10 },
    { dekad: 6,  dekad_code: '02-D3', label: 'February 21 to 28',  month: 2,  start_day: 21, end_day: 28 },
    { dekad: 6,  dekad_code: '02-D3', label: 'February 21 to 29',  month: 2,  start_day: 21, end_day: 29 },
    { dekad: 5,  dekad_code: '02-D2', label: 'February 11 to 20',  month: 2,  start_day: 11, end_day: 20 },
    { dekad: 4,  dekad_code: '02-D1', label: 'February 1 to 10',   month: 2,  start_day: 1,  end_day: 10 },
    { dekad: 3,  dekad_code: '01-D3', label: 'January 21 to 31',   month: 1,  start_day: 21, end_day: 31 },
    { dekad: 2,  dekad_code: '01-D2', label: 'January 11 to 20',   month: 1,  start_day: 11, end_day: 20 },
    { dekad: 1,  dekad_code: '01-D1', label: 'January 1 to 10',    month: 1,  start_day: 1,  end_day: 10 },
];

// ── Layer index (14 FAO-ASIS indicators with embedded legend) ─────────────────
const LAYER_INDEX = [
    {
        category: 'Vegetation Indicators', layer_label: 'Vegetation Condition Index',
        map_code: 'VCI-D/ASIS_VCI-D_<year-dekad>', dataset_indicator: 'VCI-D',
        legend_entries: [
            { color: '#9a0000', label: '< 0.15' }, { color: '#ff0000', label: '0.15 – 0.25' },
            { color: '#ff8900', label: '0.25 – 0.35' }, { color: '#ffcd66', label: '0.35 – 0.45' },
            { color: '#ffff00', label: '0.45 – 0.55' }, { color: '#66ff00', label: '0.55 – 0.65' },
            { color: '#2fcd00', label: '0.65 – 0.75' }, { color: '#009a00', label: '0.75 – 0.85' },
            { color: '#217400', label: '> 0.85' }, { color: '#666666', label: 'Missing' },
            { color: '#0000ff', label: 'Cloud' }, { color: '#ff00ff', label: 'Snow' },
        ],
    },
    {
        category: 'Vegetation Indicators', layer_label: 'Vegetation Health Index',
        map_code: 'VHI-D/ASIS_VHI-D_<year-dekad>', dataset_indicator: 'VHI-D',
        legend_entries: [
            { color: '#9a0000', label: '< 0.15' }, { color: '#ff0000', label: '0.15 – 0.25' },
            { color: '#ff8900', label: '0.25 – 0.35' }, { color: '#ffcd66', label: '0.35 – 0.45' },
            { color: '#ffff00', label: '0.45 – 0.55' }, { color: '#66ff00', label: '0.55 – 0.65' },
            { color: '#2fcd00', label: '0.65 – 0.75' }, { color: '#009a00', label: '0.75 – 0.85' },
            { color: '#217400', label: '> 0.85' }, { color: '#666666', label: 'Missing' },
            { color: '#0000ff', label: 'Cloud' }, { color: '#ff00ff', label: 'Snow' },
        ],
    },
    {
        category: 'Seasonal Indicators', layer_label: 'Agricultural Stress Index (Cropland-Season1)',
        map_code: 'ASI-D/ASIS_ASI-D_<year-dekad>_GS1_LC-C', dataset_indicator: 'ASI-D-C',
        legend_entries: [
            { color: '#2fcd00', label: '< 10%' }, { color: '#9ae713', label: '10 – 25%' },
            { color: '#ffff30', label: '25 – 40%' }, { color: '#ffba20', label: '40 – 55%' },
            { color: '#ff7510', label: '55 – 70%' }, { color: '#ff2a02', label: '70 – 85%' },
            { color: '#c40000', label: '≥ 85%' }, { color: '#000000', label: 'No data' },
            { color: '#666666', label: 'No seasonality' }, { color: '#cccccc', label: 'No cropland' },
        ],
    },
    {
        category: 'Seasonal Indicators', layer_label: 'Agricultural Stress Index (Cropland-Season2)',
        map_code: 'ASI-D/ASIS_ASI-D_<year-dekad>_GS2_LC-C', dataset_indicator: 'ASI-D-C',
        legend_entries: [
            { color: '#2fcd00', label: '< 10%' }, { color: '#9ae713', label: '10 – 25%' },
            { color: '#ffff30', label: '25 – 40%' }, { color: '#ffba20', label: '40 – 55%' },
            { color: '#ff7510', label: '55 – 70%' }, { color: '#ff2a02', label: '70 – 85%' },
            { color: '#c40000', label: '≥ 85%' }, { color: '#000000', label: 'No data' },
            { color: '#666666', label: 'No seasonality' }, { color: '#cccccc', label: 'No cropland' },
        ],
    },
    {
        category: 'Seasonal Indicators', layer_label: 'Agricultural Stress Index (Grassland-Season1)',
        map_code: 'ASI-D/ASIS_ASI-D_<year-dekad>_GS1_LC-G', dataset_indicator: 'ASI-D-G',
        legend_entries: [
            { color: '#2fcd00', label: '< 10%' }, { color: '#9ae713', label: '10 – 25%' },
            { color: '#ffff30', label: '25 – 40%' }, { color: '#ffba20', label: '40 – 55%' },
            { color: '#ff7510', label: '55 – 70%' }, { color: '#ff2a02', label: '70 – 85%' },
            { color: '#c40000', label: '≥ 85%' }, { color: '#000000', label: 'No data' },
            { color: '#666666', label: 'No seasonality' }, { color: '#cccccc', label: 'No grassland' },
        ],
    },
    {
        category: 'Seasonal Indicators', layer_label: 'Agricultural Stress Index (Grassland-Season2)',
        map_code: 'ASI-D/ASIS_ASI-D_<year-dekad>_GS2_LC-G', dataset_indicator: 'ASI-D-G',
        legend_entries: [
            { color: '#2fcd00', label: '< 10%' }, { color: '#9ae713', label: '10 – 25%' },
            { color: '#ffff30', label: '25 – 40%' }, { color: '#ffba20', label: '40 – 55%' },
            { color: '#ff7510', label: '55 – 70%' }, { color: '#ff2a02', label: '70 – 85%' },
            { color: '#c40000', label: '≥ 85%' }, { color: '#000000', label: 'No data' },
            { color: '#666666', label: 'No seasonality' }, { color: '#cccccc', label: 'No grassland' },
        ],
    },
    {
        category: 'Seasonal Indicators', layer_label: 'Drought Intensity (Cropland-Season1)',
        map_code: 'DI-D/ASIS_DI-D_<year-dekad>_GS1_LC-C', dataset_indicator: 'DI-D-C',
        legend_entries: [
            { color: '#c00000', label: '< 0.25 (Extreme)' }, { color: '#ca6664', label: '0.25 – 0.35 (Severe)' },
            { color: '#fff2cc', label: '0.35 – 0.38 (Moderate)' }, { color: '#e2efda', label: '0.38 – 0.42 (Mild)' },
            { color: '#aad08e', label: '≥ 0.42 (None)' }, { color: '#6666ff', label: 'Off season' },
            { color: '#000000', label: 'No data' }, { color: '#666666', label: 'No seasons' },
            { color: '#cccccc', label: 'No cropland' },
        ],
    },
    {
        category: 'Seasonal Indicators', layer_label: 'Drought Intensity (Cropland-Season2)',
        map_code: 'DI-D/ASIS_DI-D_<year-dekad>_GS2_LC-C', dataset_indicator: 'DI-D-C',
        legend_entries: [
            { color: '#c00000', label: '< 0.25 (Extreme)' }, { color: '#ca6664', label: '0.25 – 0.35 (Severe)' },
            { color: '#fff2cc', label: '0.35 – 0.38 (Moderate)' }, { color: '#e2efda', label: '0.38 – 0.42 (Mild)' },
            { color: '#aad08e', label: '≥ 0.42 (None)' }, { color: '#6666ff', label: 'Off season' },
            { color: '#000000', label: 'No data' }, { color: '#666666', label: 'No seasons' },
            { color: '#cccccc', label: 'No cropland' },
        ],
    },
    {
        category: 'Seasonal Indicators', layer_label: 'Drought Intensity (Grassland-Season1)',
        map_code: 'DI-D/ASIS_DI-D_<year-dekad>_GS1_LC-G', dataset_indicator: 'DI-D-G',
        legend_entries: [
            { color: '#c00000', label: '< 0.25 (Extreme)' }, { color: '#ca6664', label: '0.25 – 0.35 (Severe)' },
            { color: '#fff2cc', label: '0.35 – 0.38 (Moderate)' }, { color: '#e2efda', label: '0.38 – 0.42 (Mild)' },
            { color: '#aad08e', label: '≥ 0.42 (None)' }, { color: '#6666ff', label: 'Off season' },
            { color: '#000000', label: 'No data' }, { color: '#666666', label: 'No seasons' },
            { color: '#cccccc', label: 'No grassland' },
        ],
    },
    {
        category: 'Seasonal Indicators', layer_label: 'Drought Intensity (Grassland-Season2)',
        map_code: 'DI-D/ASIS_DI-D_<year-dekad>_GS2_LC-G', dataset_indicator: 'DI-D-G',
        legend_entries: [
            { color: '#c00000', label: '< 0.25 (Extreme)' }, { color: '#ca6664', label: '0.25 – 0.35 (Severe)' },
            { color: '#fff2cc', label: '0.35 – 0.38 (Moderate)' }, { color: '#e2efda', label: '0.38 – 0.42 (Mild)' },
            { color: '#aad08e', label: '≥ 0.42 (None)' }, { color: '#6666ff', label: 'Off season' },
            { color: '#000000', label: 'No data' }, { color: '#666666', label: 'No seasons' },
            { color: '#cccccc', label: 'No grassland' },
        ],
    },
    {
        category: 'Seasonal Indicators', layer_label: 'Mean Vegetation Health Index (Cropland-Season1)',
        map_code: 'MVHI-D/ASIS_MVHI-D_<year-dekad>_GS1_LC-C', dataset_indicator: 'MVHI-D-C',
        legend_entries: [
            { color: '#9a0000', label: '< 0.15' }, { color: '#ff0000', label: '0.15 – 0.25' },
            { color: '#ff8900', label: '0.25 – 0.35' }, { color: '#ffcd66', label: '0.35 – 0.45' },
            { color: '#ffff00', label: '0.45 – 0.55' }, { color: '#66ff00', label: '0.55 – 0.65' },
            { color: '#2fcd00', label: '0.65 – 0.75' }, { color: '#009a00', label: '0.75 – 0.85' },
            { color: '#336600', label: '> 0.85' }, { color: '#3939d7', label: 'Off season' },
            { color: '#000000', label: 'Insufficient data' }, { color: '#00c8c8', label: 'No seasons' },
            { color: '#cccccc', label: 'No cropland' },
        ],
    },
    {
        category: 'Seasonal Indicators', layer_label: 'Mean Vegetation Health Index (Cropland-Season2)',
        map_code: 'MVHI-D/ASIS_MVHI-D_<year-dekad>_GS2_LC-C', dataset_indicator: 'MVHI-D-C',
        legend_entries: [
            { color: '#9a0000', label: '< 0.15' }, { color: '#ff0000', label: '0.15 – 0.25' },
            { color: '#ff8900', label: '0.25 – 0.35' }, { color: '#ffcd66', label: '0.35 – 0.45' },
            { color: '#ffff00', label: '0.45 – 0.55' }, { color: '#66ff00', label: '0.55 – 0.65' },
            { color: '#2fcd00', label: '0.65 – 0.75' }, { color: '#009a00', label: '0.75 – 0.85' },
            { color: '#336600', label: '> 0.85' }, { color: '#3939d7', label: 'Off season' },
            { color: '#000000', label: 'Insufficient data' }, { color: '#00c8c8', label: 'No seasons' },
            { color: '#cccccc', label: 'No cropland' },
        ],
    },
    {
        category: 'Seasonal Indicators', layer_label: 'Mean Vegetation Health Index (Grassland-Season1)',
        map_code: 'MVHI-D/ASIS_MVHI-D_<year-dekad>_GS1_LC-G', dataset_indicator: 'MVHI-D-G',
        legend_entries: [
            { color: '#9a0000', label: '< 0.15' }, { color: '#ff0000', label: '0.15 – 0.25' },
            { color: '#ff8900', label: '0.25 – 0.35' }, { color: '#ffcd66', label: '0.35 – 0.45' },
            { color: '#ffff00', label: '0.45 – 0.55' }, { color: '#66ff00', label: '0.55 – 0.65' },
            { color: '#2fcd00', label: '0.65 – 0.75' }, { color: '#009a00', label: '0.75 – 0.85' },
            { color: '#336600', label: '> 0.85' }, { color: '#3939d7', label: 'Off season' },
            { color: '#000000', label: 'Insufficient data' }, { color: '#00c8c8', label: 'No seasons' },
            { color: '#cccccc', label: 'No grassland' },
        ],
    },
    {
        category: 'Seasonal Indicators', layer_label: 'Mean Vegetation Health Index (Grassland-Season2)',
        map_code: 'MVHI-D/ASIS_MVHI-D_<year-dekad>_GS2_LC-G', dataset_indicator: 'MVHI-D-G',
        legend_entries: [
            { color: '#9a0000', label: '< 0.15' }, { color: '#ff0000', label: '0.15 – 0.25' },
            { color: '#ff8900', label: '0.25 – 0.35' }, { color: '#ffcd66', label: '0.35 – 0.45' },
            { color: '#ffff00', label: '0.45 – 0.55' }, { color: '#66ff00', label: '0.55 – 0.65' },
            { color: '#2fcd00', label: '0.65 – 0.75' }, { color: '#009a00', label: '0.75 – 0.85' },
            { color: '#336600', label: '> 0.85' }, { color: '#3939d7', label: 'Off season' },
            { color: '#000000', label: 'Insufficient data' }, { color: '#00c8c8', label: 'No seasons' },
            { color: '#cccccc', label: 'No grassland' },
        ],
    },
];

// ── Helpers ───────────────────────────────────────────────────────────────────
const isLeapYear = (y) => y % 4 === 0;

function getFilteredDekadsForYear(year) {
    const today = new Date();
    const cutoffMonth = today.getMonth(); // 0-indexed; January=0
    const currentYear = today.getFullYear();
    const leap = isLeapYear(year);
    return DEKAD_INDEX.filter(d => {
        if (year === currentYear && d.month > cutoffMonth) return false;
        if (d.month === 2 && d.dekad_code === '02-D3') {
            if (leap && d.end_day === 28) return false;
            if (!leap && d.end_day === 29) return false;
        }
        return true;
    });
}

function getMostRecentDekadCode(year) {
    const dekads = getFilteredDekadsForYear(year);
    return dekads.length ? dekads[0].dekad_code : '09-D3';
}

function buildAllDataLayerList(mapCode) {
    const today = new Date();
    const currentYear = today.getFullYear();
    const results = [];
    for (let year = currentYear - 5; year <= currentYear; year++) {
        const dekads = [...getFilteredDekadsForYear(year)].reverse(); // ASC for this year
        for (const d of dekads) {
            const yearDekad = `${year}-${d.dekad_code}`;
            results.push({
                year_dekad: yearDekad,
                label: `${d.label}, ${year}`,
                layer: mapCode.replace('<year-dekad>', yearDekad),
            });
        }
    }
    return results; // oldest → newest
}

function getDefaultComparePeriods() {
    const today = new Date();
    const currentYear = today.getFullYear();
    const available = getFilteredDekadsForYear(currentYear);
    const seen = new Set();
    const unique = available.filter(d => {
        if (seen.has(d.dekad_code)) return false;
        seen.add(d.dekad_code);
        return true;
    }); // DESC order — first 3 are most recent
    return {
        0: { year: currentYear, dekad_code: unique[0]?.dekad_code || '09-D3' },
        1: { year: currentYear, dekad_code: unique[1]?.dekad_code || '09-D2' },
        2: { year: currentYear, dekad_code: unique[2]?.dekad_code || '09-D1' },
    };
}

// ── OSM tile layer factory ────────────────────────────────────────────────────
const OSM_TILE_URL = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
const OSM_ATTRIBUTION = '&copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a>';

// ── Legend component ──────────────────────────────────────────────────────────
function Legend({ entries, inline }) {
    if (!entries || !entries.length) return null;
    return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 12px', ...(inline ? {} : { padding: '8px 0', marginTop: 6 }) }}>
            {entries.map((e, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                    <div style={{
                        width: 14, height: 14, flexShrink: 0,
                        background: e.color,
                        border: '1px solid rgba(0,0,0,0.15)',
                        borderRadius: 2,
                    }} />
                    <span style={{ fontSize: 11, color: T.ink }}>{e.label}</span>
                </div>
            ))}
        </div>
    );
}

// ── Main component ────────────────────────────────────────────────────────────
function AnalyticsFaoAsis() {
    const { isAuthenticated, logout } = useContext(AuthContext);
    const navigate = useNavigate();

    // Filters
    const [selectedCountry, setSelectedCountry] = useState(2);
    const [selectedAdm1, setSelectedAdm1] = useState('');
    const [selectedAdm2, setSelectedAdm2] = useState('');
    const [adm1List, setAdm1List] = useState([]);
    const [adm2List, setAdm2List] = useState([]);
    const [selectedLayerIdx, setSelectedLayerIdx] = useState(6); // DI Cropland S1

    // Tabs
    const [activeTab, setActiveTab] = useState('compare');

    // Compare
    const [comparePeriods, setComparePeriods] = useState(() => getDefaultComparePeriods());
    const [compareResult, setCompareResult] = useState(null);
    const [compareLoading, setCompareLoading] = useState(false);
    const [compareError, setCompareError] = useState(null);

    // All Data
    const [allDataLayerList, setAllDataLayerList] = useState([]);
    const [allDataSliderIdx, setAllDataSliderIdx] = useState(0);
    const [allDataResult, setAllDataResult] = useState(null);
    const [allDataLoading, setAllDataLoading] = useState(false);
    const [allDataError, setAllDataError] = useState(null);
    const allDataDebounceRef = useRef(null);

    // Map DOM refs (callback-ref pattern for compare maps)
    const compareMapDivs = useRef([null, null, null]);
    const compareMapInst = useRef([null, null, null]);
    const compareImgLayers = useRef([null, null, null]);
    const compareCampGroups = useRef([null, null, null]);

    const allDataMapDiv = useRef(null);
    const allDataMapInst = useRef(null);
    const allDataImgLayer = useRef(null);
    const allDataCampGroup = useRef(null);
    const compareZoomSync = useRef(false);
    const compareBoundaryLayers = useRef([null, null, null]);
    const allDataBoundaryLayer = useRef(null);
    const geoCache = useRef({});

    // ── Auth guard ──────────────────────────────────────────────────────────
    useEffect(() => {
        if (!isAuthenticated) navigate('/login');
    }, [isAuthenticated, navigate]);

    // ── API helpers ─────────────────────────────────────────────────────────
    const getHeaders = () => {
        const token = localStorage.getItem('profishBlue_authToken');
        const expiry = localStorage.getItem('profishBlue_tokenExpiry');
        if (!token || !expiry || Date.now() > parseInt(expiry, 10)) {
            logout();
            navigate('/login');
            return {};
        }
        return { 'Content-Type': 'application/json', Authorization: token };
    };

    // ── Boundary overlay helper ──────────────────────────────────────────────
    // Fetches the full adm1 or adm2 geojson once per country (cached), then
    // filters features client-side using adm1_name / name properties.
    const drawBoundaries = async (countryId, adm1Id, adm2Id) => {
        const level = adm1Id ? 2 : 1;
        const cacheKey = adm2Id
            ? `${countryId}_adm2_d${adm2Id}`
            : adm1Id
                ? `${countryId}_adm2_p${adm1Id}`
                : `${countryId}_adm1`;
        let geo;
        if (geoCache.current[cacheKey]) {
            geo = geoCache.current[cacheKey];
        } else {
            try {
                const params = new URLSearchParams({ country_id: countryId, level });
                if (adm2Id) params.append('adm2_id', adm2Id);
                else if (adm1Id) params.append('adm1_id', adm1Id);
                const url = `${envConfig.API_URL}/riskmap/geojson?${params.toString()}`;
                const resp = await fetch(url, { headers: getHeaders() });
                geo = await resp.json();
                geoCache.current[cacheKey] = geo;
            } catch {
                return;
            }
        }

        const style = { color: T.navy, weight: 1, fillOpacity: 0, opacity: 0.55 };
        const prefix = level === 1 ? 'Province' : 'District';
        const makeLayer = () => L.geoJSON(geo, {
            style,
            onEachFeature: (feature, layer) => {
                const name = feature?.properties?.name;
                if (name) layer.bindTooltip(`${prefix}: ${name}`, { sticky: true });
            },
        });
        compareMapInst.current.forEach((map, i) => {
            if (!map) return;
            if (compareBoundaryLayers.current[i]) map.removeLayer(compareBoundaryLayers.current[i]);
            compareBoundaryLayers.current[i] = makeLayer().addTo(map);
        });
        if (allDataMapInst.current) {
            if (allDataBoundaryLayer.current) allDataMapInst.current.removeLayer(allDataBoundaryLayer.current);
            allDataBoundaryLayer.current = makeLayer().addTo(allDataMapInst.current);
        }
    };

    // ── Fetch adm1 when country changes ─────────────────────────────────────
    useEffect(() => {
        if (!isAuthenticated) return;
        setSelectedAdm1(''); setSelectedAdm2(''); setAdm1List([]); setAdm2List([]);
        fetch(`${envConfig.API_URL}/lookups/locations/adm1?country_id=${selectedCountry}`, { headers: getHeaders() })
            .then(r => r.json())
            .then(d => setAdm1List(d.data || []))
            .catch(() => {});
    }, [selectedCountry, isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Fetch adm2 when adm1 changes ─────────────────────────────────────────
    useEffect(() => {
        setSelectedAdm2(''); setAdm2List([]);
        if (!selectedAdm1 || !isAuthenticated) return;
        fetch(`${envConfig.API_URL}/lookups/locations/adm2?country_id=${selectedCountry}&adm1_id=${selectedAdm1}`, { headers: getHeaders() })
            .then(r => r.json())
            .then(d => setAdm2List(d.data || []))
            .catch(() => {});
    }, [selectedAdm1, selectedCountry, isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Redraw boundaries when country / province / district selection changes ─
    useEffect(() => {
        if (!isAuthenticated) return;
        drawBoundaries(selectedCountry, selectedAdm1, selectedAdm2);
    }, [selectedCountry, selectedAdm1, selectedAdm2, isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Init All Data layer list on mount and when layer changes ─────────────
    useEffect(() => {
        const layer = LAYER_INDEX[selectedLayerIdx];
        const list = buildAllDataLayerList(layer.map_code);
        setAllDataLayerList(list);
        setAllDataSliderIdx(list.length - 1); // default to most recent
        setAllDataResult(null);
    }, [selectedLayerIdx]);

    // ── Init compare maps once on mount ──────────────────────────────────────
    useEffect(() => {
        const created = [];
        for (let i = 0; i < 3; i++) {
            const div = compareMapDivs.current[i];
            if (!div || compareMapInst.current[i]) continue;
            const { center, zoom } = COUNTRY_CONFIG[2]; // Zambia default
            const map = L.map(div, { center, zoom, attributionControl: false });
            L.tileLayer(OSM_TILE_URL, { attribution: OSM_ATTRIBUTION, maxZoom: 18 }).addTo(map);
            compareMapInst.current[i] = map;
            created.push(i);
        }
        // sync pan + zoom: move fires on every frame during drag and on zoom changes
        compareMapInst.current.forEach((map, i) => {
            if (!map) return;
            map.on('move', () => {
                if (compareZoomSync.current) return;
                compareZoomSync.current = true;
                const center = map.getCenter();
                const zoom = map.getZoom();
                compareMapInst.current.forEach((other, j) => {
                    if (j !== i && other) other.setView(center, zoom, { animate: false });
                });
                compareZoomSync.current = false;
            });
        });
        // boundary effect runs before this (defined earlier), so maps weren't ready — draw now
        drawBoundaries(selectedCountry, selectedAdm1, selectedAdm2);
        return () => {
            created.forEach(i => {
                if (compareMapInst.current[i]) {
                    compareMapInst.current[i].remove();
                    compareMapInst.current[i] = null;
                }
            });
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Init All Data map when tab activates ─────────────────────────────────
    useEffect(() => {
        if (activeTab !== 'alldata' || allDataMapInst.current) return;
        const timer = setTimeout(() => {
            const div = allDataMapDiv.current;
            if (!div) return;
            const { center, zoom } = COUNTRY_CONFIG[selectedCountry];
            const map = L.map(div, { center, zoom, attributionControl: false });
            L.tileLayer(OSM_TILE_URL, { attribution: OSM_ATTRIBUTION, maxZoom: 18 }).addTo(map);
            allDataMapInst.current = map;
            drawBoundaries(selectedCountry, selectedAdm1, selectedAdm2);
        }, 80);
        return () => clearTimeout(timer);
    }, [activeTab, selectedCountry]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Pan compare maps when country changes ────────────────────────────────
    useEffect(() => {
        const { center, zoom } = COUNTRY_CONFIG[selectedCountry] || {};
        if (!center) return;
        compareMapInst.current.forEach(map => { if (map) map.setView(center, zoom); });
        if (allDataMapInst.current) allDataMapInst.current.setView(center, zoom);
    }, [selectedCountry]);

    // ── Apply compare result to the 3 maps ───────────────────────────────────
    useEffect(() => {
        if (!compareResult) return;
        const [minLon, minLat, maxLon, maxLat] = compareResult.bbox_4326;
        const bounds = [[minLat, minLon], [maxLat, maxLon]];

        compareResult.layers.forEach((lyr, idx) => {
            const i = lyr.compare_id - 1; // compare_id is 1-indexed
            const map = compareMapInst.current[i];
            if (!map) return;

            if (compareImgLayers.current[i]) { map.removeLayer(compareImgLayers.current[i]); }
            if (compareCampGroups.current[i]) { map.removeLayer(compareCampGroups.current[i]); }

            compareImgLayers.current[i] = L.imageOverlay(lyr.gee_url, bounds, { opacity: 0.85 }).addTo(map);

            const grp = L.layerGroup();
            (compareResult.camps || []).forEach(c => {
                L.circleMarker([c.latitude, c.longitude], {
                    radius: 4, color: T.navy, fillColor: '#ffffff', fillOpacity: 0.9, weight: 1.5,
                }).bindTooltip(`Camp: ${c.name}`, { permanent: false }).addTo(grp);
            });
            compareCampGroups.current[i] = grp;
            grp.addTo(map);
            map.fitBounds(bounds, { padding: [8, 8] });
        });
    }, [compareResult]);

    // ── Apply All Data result to the single map ──────────────────────────────
    useEffect(() => {
        if (!allDataResult) return;
        const map = allDataMapInst.current;
        if (!map) return;

        const [minLon, minLat, maxLon, maxLat] = allDataResult.bbox_4326;
        const bounds = [[minLat, minLon], [maxLat, maxLon]];

        if (allDataImgLayer.current) { map.removeLayer(allDataImgLayer.current); }
        if (allDataCampGroup.current) { map.removeLayer(allDataCampGroup.current); }

        allDataImgLayer.current = L.imageOverlay(allDataResult.gee_url, bounds, { opacity: 0.85 }).addTo(map);

        const grp = L.layerGroup();
        (allDataResult.camps || []).forEach(c => {
            L.circleMarker([c.latitude, c.longitude], {
                radius: 4, color: T.navy, fillColor: '#ffffff', fillOpacity: 0.9, weight: 1.5,
            }).bindTooltip(c.name, { permanent: false }).addTo(grp);
        });
        allDataCampGroup.current = grp;
        grp.addTo(map);
        map.fitBounds(bounds, { padding: [8, 8] });
    }, [allDataResult]);

    // ── Fetch compare data ───────────────────────────────────────────────────
    const fetchCompare = async () => {
        setCompareLoading(true);
        setCompareError(null);
        const layer = LAYER_INDEX[selectedLayerIdx];
        const periods = {};
        for (let i = 0; i < 3; i++) {
            const { year, dekad_code } = comparePeriods[i];
            periods[String(i + 1)] = `${year}-${dekad_code}`;
        }
        try {
            const resp = await fetch(`${envConfig.API_URL}/riskmap/fao_asis_img_group`, {
                method: 'POST',
                headers: getHeaders(),
                body: JSON.stringify({
                    country_id: selectedCountry,
                    adm1_id: selectedAdm1 ? Number(selectedAdm1) : null,
                    adm2_id: selectedAdm2 ? Number(selectedAdm2) : null,
                    map_code: layer.map_code,
                    dataset_indicator: layer.dataset_indicator,
                    periods,
                }),
            });
            const data = await resp.json();
            if (data.success) setCompareResult(data.data);
            else setCompareError(data.message || 'Failed to load imagery');
        } catch (e) {
            setCompareError('Network error. Please try again.');
        } finally {
            setCompareLoading(false);
        }
    };

    // ── Fetch all data (single dekad) ────────────────────────────────────────
    const fetchAllData = async (idx) => {
        const list = allDataLayerList;
        if (!list.length) return;
        const entry = list[idx !== undefined ? idx : allDataSliderIdx];
        if (!entry) return;
        setAllDataLoading(true);
        setAllDataError(null);
        const layer = LAYER_INDEX[selectedLayerIdx];
        try {
            const resp = await fetch(`${envConfig.API_URL}/riskmap/fao_asis_imagery`, {
                method: 'POST',
                headers: getHeaders(),
                body: JSON.stringify({
                    country_id: selectedCountry,
                    adm1_id: selectedAdm1 ? Number(selectedAdm1) : null,
                    adm2_id: selectedAdm2 ? Number(selectedAdm2) : null,
                    layer: entry.layer,
                    dataset_indicator: layer.dataset_indicator,
                }),
            });
            const data = await resp.json();
            if (data.success) setAllDataResult(data.data);
            else setAllDataError(data.message || 'Failed to load imagery');
        } catch (e) {
            setAllDataError('Network error. Please try again.');
        } finally {
            setAllDataLoading(false);
        }
    };

    // ── Auto-load compare on mount ───────────────────────────────────────────
    useEffect(() => {
        if (isAuthenticated) fetchCompare();
    }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Debounce all-data slider fetches ─────────────────────────────────────
    useEffect(() => {
        if (!allDataLayerList.length || activeTab !== 'alldata') return;
        if (allDataDebounceRef.current) clearTimeout(allDataDebounceRef.current);
        allDataDebounceRef.current = setTimeout(() => fetchAllData(allDataSliderIdx), 700);
        return () => clearTimeout(allDataDebounceRef.current);
    }, [allDataSliderIdx, allDataLayerList, activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Fetch all-data on tab activation if not yet loaded ───────────────────
    useEffect(() => {
        if (activeTab === 'alldata' && !allDataResult && !allDataLoading && allDataLayerList.length) {
            fetchAllData(allDataSliderIdx);
        }
    }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Invalidate map size after all-data loading overlay clears ────────────
    useEffect(() => {
        if (!allDataLoading && allDataMapInst.current) {
            allDataMapInst.current.invalidateSize();
        }
    }, [allDataLoading]);

    // ── Year options ──────────────────────────────────────────────────────────
    const yearOptions = Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - i);

    const handleComparePeriodChange = (mapIdx, field, value) => {
        setComparePeriods(prev => {
            const updated = { ...prev, [mapIdx]: { ...prev[mapIdx], [field]: Number(value) } };
            if (field === 'year') {
                const code = getMostRecentDekadCode(Number(value));
                updated[mapIdx].dekad_code = code;
            }
            return updated;
        });
    };

    const labelFor = (mapIdx) => {
        const { year, dekad_code } = comparePeriods[mapIdx] || {};
        if (!year || !dekad_code) return '';
        const d = DEKAD_INDEX.find(x => x.dekad_code === dekad_code && !x.label.includes('29'));
        return d ? `${d.label}, ${year}` : `${year}-${dekad_code}`;
    };

    const currentLayerLegend = LAYER_INDEX[selectedLayerIdx]?.legend_entries || [];
    const allDataCurrentEntry = allDataLayerList[allDataSliderIdx];

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <div className="wrapper pt-100px one">
            <div className="container-fluid">
                <div className="row pt-3">
                    <div className="col-sm-12 col-md-12 col-lg-12">
                        <div className="box-config">

                            {/* Page header */}
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <div>
                                    <h5 style={{ color: T.navy, fontWeight: 700, margin: 0 }}>FAO ASIS Analytics</h5>
                                    <small style={{ color: T.muted }}>
                                        FAO Earth Observation — Agricultural Stress Index System
                                    </small>
                                </div>
                            </div>

                            {/* Filters row */}
                            <div className="row mb-3 align-items-end">
                                <div className="col-sm-12 col-md-2">
                                    <div className="form-group me-2">
                                        <label className="form-label" style={{ fontSize: 12, color: T.muted, fontWeight: 600 }}>Country</label>
                                        <select
                                            className="form-select first-filter mt-1"
                                            value={selectedCountry}
                                            onChange={e => setSelectedCountry(Number(e.target.value))}
                                        >
                                            <option value={1}>Malawi</option>
                                            <option value={2}>Zambia</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="col-sm-12 col-md-2">
                                    <div className="form-group me-2">
                                        <label className="form-label" style={{ fontSize: 12, color: T.muted, fontWeight: 600 }}>Province</label>
                                        <select
                                            className="form-select first-filter mt-1"
                                            value={selectedAdm1}
                                            onChange={e => setSelectedAdm1(e.target.value)}
                                        >
                                            <option value="">All Provinces</option>
                                            {adm1List.map(a => (
                                                <option key={a.adm1_id} value={a.adm1_id}>{a.adm1}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                <div className="col-sm-12 col-md-2">
                                    <div className="form-group me-2">
                                        <label className="form-label" style={{ fontSize: 12, color: T.muted, fontWeight: 600 }}>District</label>
                                        <select
                                            className="form-select first-filter mt-1"
                                            value={selectedAdm2}
                                            onChange={e => setSelectedAdm2(e.target.value)}
                                            disabled={!selectedAdm1}
                                        >
                                            <option value="">All Districts</option>
                                            {adm2List.map(a => (
                                                <option key={a.adm2_id} value={a.adm2_id}>{a.adm2}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                <div className="col-sm-12 col-md-4">
                                    <div className="form-group me-2">
                                        <label className="form-label" style={{ fontSize: 12, color: T.muted, fontWeight: 600 }}>
                                            Indicator&nbsp;
                                            <a href="https://www.fao.org/giews/earthobservation/" target="_blank" rel="noreferrer" style={{ fontSize: 11 }}>
                                                (FAO Earth Observation)
                                            </a>
                                        </label>
                                        <select
                                            className="form-select first-filter mt-1"
                                            value={selectedLayerIdx}
                                            onChange={e => setSelectedLayerIdx(Number(e.target.value))}
                                        >
                                            {Object.entries(
                                                LAYER_INDEX.reduce((acc, lyr, i) => {
                                                    (acc[lyr.category] = acc[lyr.category] || []).push({ lyr, i });
                                                    return acc;
                                                }, {})
                                            ).map(([cat, items]) => (
                                                <optgroup key={cat} label={cat}>
                                                    {items.map(({ lyr, i }) => (
                                                        <option key={i} value={i}>{lyr.layer_label}</option>
                                                    ))}
                                                </optgroup>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>

                            {/* Tab buttons + Load Maps / Slider on one line */}
                            <div className="d-flex align-items-center mb-2" style={{ gap: 6 }}>
                                <button
                                    className="btn btn-sm"
                                    style={{
                                        background: activeTab === 'compare' ? T.navy2 : 'transparent',
                                        color: activeTab === 'compare' ? '#fff' : T.navy2,
                                        border: `1px solid ${T.navy2}`,
                                        borderRadius: 2,
                                        fontSize: 12,
                                        fontWeight: 700,
                                        padding: '5px 14px',
                                        letterSpacing: '0.3px',
                                    }}
                                    onClick={() => setActiveTab('compare')}
                                >
                                    Compare
                                </button>
                                <button
                                    className="btn btn-sm"
                                    style={{
                                        background: activeTab === 'alldata' ? T.navy2 : 'transparent',
                                        color: activeTab === 'alldata' ? '#fff' : T.navy2,
                                        border: `1px solid ${T.navy2}`,
                                        borderRadius: 2,
                                        fontSize: 12,
                                        fontWeight: 700,
                                        padding: '5px 14px',
                                        letterSpacing: '0.3px',
                                    }}
                                    onClick={() => setActiveTab('alldata')}
                                >
                                    All Data
                                </button>

                                <div className="d-flex align-items-center" style={{ flex: 1 }}>
                                    {activeTab === 'compare' && (
                                        <>
                                            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap', padding: '0 12px' }}>
                                                <Legend entries={compareResult?.legend_entries || currentLayerLegend} inline />
                                            </div>
                                            <button
                                                className="btn btn-sm"
                                                style={{ background: T.navy2, color: '#fff', fontSize: 12, padding: '4px 14px', flexShrink: 0 }}
                                                onClick={fetchCompare}
                                                disabled={compareLoading}
                                            >
                                                {compareLoading ? (
                                                    <><span className="spinner-border spinner-border-sm me-1" />Loading…</>
                                                ) : 'Load Maps'}
                                            </button>
                                        </>
                                    )}
                                    {activeTab === 'alldata' && allDataLayerList.length > 0 && (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, maxWidth: 420, marginLeft: 16 }}>
                                            <span style={{ fontSize: 10, color: T.muted, whiteSpace: 'nowrap' }}>
                                                {allDataLayerList[0]?.year_dekad}
                                            </span>
                                            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                                                <span style={{ fontSize: 12, fontWeight: 600, color: T.ink }}>
                                                    {allDataCurrentEntry ? allDataCurrentEntry.label : '—'}
                                                </span>
                                                <input
                                                    type="range"
                                                    min={0}
                                                    max={allDataLayerList.length - 1}
                                                    value={allDataSliderIdx}
                                                    onChange={e => setAllDataSliderIdx(Number(e.target.value))}
                                                    style={{ width: '100%', accentColor: T.navy2 }}
                                                />
                                            </div>
                                            <span style={{ fontSize: 10, color: T.muted, whiteSpace: 'nowrap' }}>
                                                {allDataLayerList[allDataLayerList.length - 1]?.year_dekad}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* ── Compare Tab ──────────────────────────────────────── */}
                            <div style={{ display: activeTab === 'compare' ? 'block' : 'none' }}>
                                {compareError && (
                                    <div className="alert alert-danger py-2 mb-2" style={{ fontSize: 12 }}>{compareError}</div>
                                )}

                                <div className="row">
                                    {[0, 1, 2].map(i => {
                                        const { year, dekad_code } = comparePeriods[i] || {};
                                        const dekadsForYear = year ? getFilteredDekadsForYear(year) : [];
                                        const seenCodes = new Set();
                                        const uniqueDekads = dekadsForYear.filter(d => {
                                            if (seenCodes.has(d.dekad_code)) return false;
                                            seenCodes.add(d.dekad_code);
                                            return true;
                                        });
                                        return (
                                            <div key={i} className="col-sm-12 col-md-4">
                                                {/* Year / Dekad selectors */}
                                                <div className="row mb-2">
                                                    <div className="col-6">
                                                        <div className="form-group">
                                                            <label className="form-label" style={{ fontSize: 11, color: T.muted }}>Year</label>
                                                            <select
                                                                className="form-select mt-1"
                                                                style={{ fontSize: 12 }}
                                                                value={year || ''}
                                                                onChange={e => handleComparePeriodChange(i, 'year', e.target.value)}
                                                            >
                                                                {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
                                                            </select>
                                                        </div>
                                                    </div>
                                                    <div className="col-6">
                                                        <div className="form-group">
                                                            <label className="form-label" style={{ fontSize: 11, color: T.muted }}>Dekad</label>
                                                            <select
                                                                className="form-select mt-1"
                                                                style={{ fontSize: 12 }}
                                                                value={dekad_code || ''}
                                                                onChange={e => handleComparePeriodChange(i, 'dekad_code', e.target.value)}
                                                            >
                                                                {uniqueDekads.map(d => (
                                                                    <option key={d.dekad_code} value={d.dekad_code}>{d.label}</option>
                                                                ))}
                                                            </select>
                                                        </div>
                                                    </div>
                                                </div>
                                                {/* Map div */}
                                                <div style={{ position: 'relative' }}>
                                                    <div
                                                        ref={el => { compareMapDivs.current[i] = el; }}
                                                        style={{ width: '100%', height: '40vh', border: `1px solid ${T.line}`, borderRadius: 4 }}
                                                    />
                                                    {compareLoading && (
                                                        <div style={{
                                                            position: 'absolute', inset: 0, borderRadius: 4,
                                                            background: 'rgba(255,255,255,0.55)',
                                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                            zIndex: 500,
                                                        }}>
                                                            <span className="spinner-border" style={{ width: 28, height: 28, color: T.navy2 }} />
                                                        </div>
                                                    )}
                                                    {labelFor(i) && (
                                                        <div style={{
                                                            position: 'absolute', bottom: 8, right: 8, zIndex: 400,
                                                            background: 'rgba(255,255,255,0.88)',
                                                            borderRadius: 3, padding: '2px 7px',
                                                            fontSize: 11, color: T.ink, fontWeight: 600,
                                                            boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
                                                            pointerEvents: 'none',
                                                        }}>
                                                            {labelFor(i)}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>

                            </div>

                            {/* ── All Data Tab ──────────────────────────────────────── */}
                            <div style={{ display: activeTab === 'alldata' ? 'block' : 'none' }}>
                                {allDataError && (
                                    <div className="alert alert-danger py-2 mb-2" style={{ fontSize: 12 }}>{allDataError}</div>
                                )}

                                {/* Map */}
                                <div style={{ position: 'relative' }}>
                                    <div
                                        ref={el => { allDataMapDiv.current = el; }}
                                        style={{ width: '100%', height: '70vh', border: `1px solid ${T.line}`, borderRadius: 4, background: '#d4dadc' }}
                                    />
                                    {allDataLoading && (
                                        <div style={{
                                            position: 'absolute', inset: 0, borderRadius: 4,
                                            background: 'rgba(255,255,255,0.55)',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            zIndex: 500,
                                        }}>
                                            <span className="spinner-border" style={{ width: 28, height: 28, color: T.navy2 }} />
                                        </div>
                                    )}
                                    {allDataResult && (
                                        <div style={{
                                            position: 'absolute', bottom: 12, right: 12, zIndex: 400,
                                            background: 'rgba(255,255,255,0.92)', borderRadius: 4,
                                            padding: '8px 12px', boxShadow: '0 1px 4px rgba(0,0,0,0.18)',
                                            maxWidth: 200,
                                        }}>
                                            <div style={{ fontSize: 10, fontWeight: 700, color: T.muted, marginBottom: 4 }}>
                                                {LAYER_INDEX[selectedLayerIdx]?.layer_label}
                                            </div>
                                            <Legend entries={allDataResult.legend_entries || currentLayerLegend} />
                                        </div>
                                    )}
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            </div>
            <Footer />
        </div>
    );
}

export default AnalyticsFaoAsis;
